import { Controller, useForm } from 'react-hook-form';
import { Row, TextField } from '@tempmee/react';
import { useGetMe, useUpdateMe } from '~/domain/CurrentIdentity';
import type { IUpdateMeApiPayload } from '~/domain/CurrentIdentity/types/UsersMe.api';
import { getPersonNameRules } from '~/utils/String';
import { SectionEditDialog } from '~/components/shared/AccountArea';

const TITLE = 'Edit Personal Information';
const DATA_CY = 'edit-personal-information';

// The sign-up form's name rules, plus no blank-only name; auth-service itself
// accepts up to 255 characters.
const nameRules = (label: string) => ({
  ...getPersonNameRules(label),
  validate: (value: string) => !!value.trim() || `${label} is required`,
});

interface IPersonalInformationValues {
  firstName: string;
  lastName: string;
}

interface IPersonalInformationModalProps {
  email: string;
  phone: string;
  onClose: () => void;
}

interface IPersonalInformationFormProps
  extends IPersonalInformationModalProps,
    IPersonalInformationValues {}

const PersonalInformationForm = ({
  firstName,
  lastName,
  email,
  phone,
  onClose,
}: IPersonalInformationFormProps) => {
  const { control, handleSubmit, formState } = useForm<IPersonalInformationValues>({
    defaultValues: { firstName, lastName },
    mode: 'onChange',
  });
  // Read both up front: RHF only tracks the form-state fields a render reads,
  // and `isDirty && isValid` would skip subscribing to `isValid`.
  const { isDirty, isValid, dirtyFields } = formState;
  const updateMe = useUpdateMe();

  // Only the changed names go out; the handler needs at least one.
  const save = handleSubmit(async (values) => {
    const payload: IUpdateMeApiPayload = {
      ...(dirtyFields.firstName && { firstName: values.firstName.trim() }),
      ...(dirtyFields.lastName && { lastName: values.lastName.trim() }),
    };
    await updateMe.mutateAsync(payload);
    onClose();
  });

  return (
    <SectionEditDialog
      title={TITLE}
      dataCy={DATA_CY}
      onClose={onClose}
      onSave={save}
      canSave={isDirty && isValid}
    >
      <Row gap={2}>
        <Controller
          name="firstName"
          control={control}
          rules={nameRules('First name')}
          render={({ field, fieldState }) => (
            <TextField
              label="First Name"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              data-cy="input-first-name"
              {...field}
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          rules={nameRules('Last name')}
          render={({ field, fieldState }) => (
            <TextField
              label="Last Name"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              data-cy="input-last-name"
              {...field}
            />
          )}
        />
      </Row>
      <TextField label="Email" value={email} disabled readOnly data-cy="input-email" />
      <TextField label="Phone" value={phone} disabled readOnly data-cy="input-phone" />
    </SectionEditDialog>
  );
};

/**
 * The person's own first and last name, saved through `PATCH /auth-service/users/me`
 * (Figma 7235-92248). Email and phone show disabled.
 *
 * `useForm` keeps the defaults it mounts with, so the form waits for this visit's
 * read and starts from auth-service only, never from a cached person or the office
 * record: a stand-in name would be written back with the next save.
 */
export const PersonalInformationModal = (props: IPersonalInformationModalProps) => {
  const { data: me, isFetchedAfterMount } = useGetMe();

  if (!isFetchedAfterMount) {
    return (
      <SectionEditDialog
        title={TITLE}
        dataCy={DATA_CY}
        onClose={props.onClose}
        onSave={async () => {}}
        canSave={false}
        isLoading
      />
    );
  }

  return (
    <PersonalInformationForm
      {...props}
      firstName={me?.firstName ?? ''}
      lastName={me?.lastName ?? ''}
    />
  );
};
